import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import AdminLayout from "@/components/admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

interface WeeklyStats {
  weekStart: string;
  weekEnd: string;
  newUsers: number;
  deposits: number;
  customPuzzles: number;
  purchasedHints: number;
}

interface StatsResponse {
  stats: WeeklyStats[];
  total: {
    users: number;
    deposits: number;
    customPuzzles: number;
    purchasedHints: number;
  };
}

const WEEKS_PER_PAGE = 8;

export default function AdminStatsPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<StatsResponse>({
    queryKey: ["/api/admin/stats", { page }],
    queryFn: async () => {
      const response = await fetch(`/api/admin/stats?page=${page}`);
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      return response.json();
    },
  });

  // Format data for charts - most recent weeks first
  const chartData = data?.stats
    ? [...data.stats].reverse().map(week => ({
        name: formatDateRange(week.weekStart, week.weekEnd),
        users: week.newUsers,
        deposits: week.deposits,
        puzzles: week.customPuzzles,
        hints: week.purchasedHints,
      }))
    : [];

  const totalPages = data?.stats ? Math.ceil(data.stats.length / WEEKS_PER_PAGE) : 0;

  return (
    <AdminLayout>
      <Card className="bg-zinc-900 border-zinc-800 mb-6">
        <CardHeader>
          <CardTitle>Platform Statistics</CardTitle>
          <CardDescription>Weekly statistics for user activity and engagement</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard 
                  title="Total Users" 
                  value={data?.total.users || 0} 
                  description="All time users"
                />
                <StatCard 
                  title="Total Deposits" 
                  value={data?.total.deposits || 0} 
                  description="All time deposits"
                />
                <StatCard 
                  title="Custom Puzzles" 
                  value={data?.total.customPuzzles || 0} 
                  description="All time puzzles created"
                />
                <StatCard 
                  title="Purchased Hints" 
                  value={data?.total.purchasedHints || 0} 
                  description="All time hint purchases"
                />
              </div>

              {/* Charts */}
              <div className="space-y-8 mb-6">
                <ChartSection 
                  title="New Users per Week" 
                  data={chartData} 
                  dataKey="users" 
                  color="#4f46e5"
                />
                
                <ChartSection 
                  title="Deposits per Week" 
                  data={chartData} 
                  dataKey="deposits" 
                  color="#10b981"
                />
                
                <ChartSection 
                  title="Custom Puzzles Created per Week" 
                  data={chartData} 
                  dataKey="puzzles" 
                  color="#f59e0b"
                />
                
                <ChartSection 
                  title="Hints Purchased per Week" 
                  data={chartData} 
                  dataKey="hints" 
                  color="#ef4444"
                />
              </div>

              {/* Raw Data Table */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Weekly Breakdown</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Week</TableHead>
                      <TableHead>New Users</TableHead>
                      <TableHead>Deposits</TableHead>
                      <TableHead>Custom Puzzles</TableHead>
                      <TableHead>Purchased Hints</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.stats.map((week, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {formatDateRange(week.weekStart, week.weekEnd)}
                        </TableCell>
                        <TableCell>{week.newUsers}</TableCell>
                        <TableCell>{week.deposits}</TableCell>
                        <TableCell>{week.customPuzzles}</TableCell>
                        <TableCell>{week.purchasedHints}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between mt-4 space-x-2">
                  <div className="text-sm text-zinc-500">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

// Helper component for the stat cards
interface StatCardProps {
  title: string;
  value: number;
  description: string;
}

function StatCard({ title, value, description }: StatCardProps) {
  return (
    <div className="bg-zinc-800 rounded-lg p-4">
      <h3 className="text-sm font-medium text-zinc-400">{title}</h3>
      <p className="text-2xl font-bold mt-1">{value.toLocaleString()}</p>
      <p className="text-xs text-zinc-500 mt-1">{description}</p>
    </div>
  );
}

// Helper component for the chart sections
interface ChartSectionProps {
  title: string;
  data: Array<{
    name: string;
    users?: number;
    deposits?: number;
    puzzles?: number;
    hints?: number;
    [key: string]: any;
  }>;
  dataKey: string;
  color: string;
}

function ChartSection({ title, data, dataKey, color }: ChartSectionProps) {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis 
              dataKey="name" 
              tick={{ fill: '#aaa', fontSize: 12 }} 
              angle={-45} 
              textAnchor="end"
            />
            <YAxis tick={{ fill: '#aaa' }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#222', 
                border: '1px solid #444',
                borderRadius: '4px' 
              }} 
            />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Helper function to format date range
function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  
  return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}
